import { type QueryClient, useMutation, useQueryClient } from '@tanstack/react-query'

import type { PatchScheduleOverrideBody, PostScheduleOverrideBody } from '@snootsvet/shared'

import { useOsApi } from '@/frontend/hooks/api/use-os-api'

/**
 * Invalidates everything that reflects a cover change: the admin list and the
 * clinic schedule (so the appointments calendar + admin rota layer refetch).
 * Centralized here so the keys live in one place across create/update/delete.
 */
function invalidateScheduleOverrides(queryClient: QueryClient) {
  queryClient.invalidateQueries({ queryKey: ['admin', 'schedule-overrides'] })
  queryClient.invalidateQueries({ queryKey: ['clinic-schedule'] })
}

/** Creates a schedule override (cover). */
export function useCreateScheduleOverride() {
  const api = useOsApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: PostScheduleOverrideBody) => {
      const response = await api.post('/schedules/overrides', { body })
      if (!response.success) throw new Error(response.error.message)
    },
    onSuccess: () => invalidateScheduleOverrides(queryClient),
  })
}

/** Updates an existing schedule override. */
export function useUpdateScheduleOverride() {
  const api = useOsApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: PatchScheduleOverrideBody }) => {
      const response = await api.patch('/schedules/overrides/:id', { params: { id }, body })
      if (!response.success) throw new Error(response.error.message)
    },
    onSuccess: () => invalidateScheduleOverrides(queryClient),
  })
}

/** Deletes a schedule override. */
export function useDeleteScheduleOverride() {
  const api = useOsApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete('/schedules/overrides/:id', { params: { id } })
      if (!response.success) throw new Error(response.error.message)
    },
    onSuccess: () => invalidateScheduleOverrides(queryClient),
  })
}
